<template>
  <button
    v-if="media"
    @click="startMedia"
    :disabled="isActionLoading"
    :class="[
      'bootcamp-level',
      isBeingWatched && 'bootcamp-level__selected',
      contentIsFinished && 'bootcamp-level__finished',
    ]"
  >
    <div class="bootcamp-level__icon-wrapper">
      <ActionLoader
        v-if="levelIsLoading"
        :size="0.5"
        loaderClass="bootcamp-level__loader"
      />
      <component v-else :is="contentIcon" class="bootcamp-level__icon" />
    </div>
    <div>
      <ActionLoader
        v-if="!title"
        :size="0.5"
        loaderClass="bootcamp-level__loader--title"
      />
      <p class="bootcamp-level__title">{{ title }}</p>
    </div>
    <p v-if="contentDuration" class="bootcamp-level__duration">
      {{ contentDuration }}
    </p>
  </button>
</template>

<script>
import { mapState } from 'vuex'
import { duration } from '@/helpers'
import CONSTANTS from '@/constants'
import VideoIcon from '@/assets/icons/bootcamps/video_icon.svg'
import QuizIcon from '@/assets/icons/bootcamps/quiz_icon.svg'
import LabIcon from '@/assets/icons/bootcamps/lab_icon.svg'
import ActionLoader from '@/components/shared/ActionLoader.vue'

export default {
  name: 'Level',
  components: {
    VideoIcon: { ...VideoIcon, name: 'VideoIcon' },
    QuizIcon: { ...QuizIcon, name: 'QuizIcon' },
    LabIcon: { ...LabIcon, name: 'LabIcon' },
    ActionLoader,
  },
  props: {
    media: {
      type: Object,
      required: true,
    },
  },
  data: () => {
    return {
      levelIsLoading: false,
    }
  },
  watch: {
    isActionLoading(value) {
      if (!value) {
        this.levelIsLoading = value
      }
    },
  },
  computed: {
    ...mapState({
      playingVideoId: state => state.content.playingVideoId,
      isActionLoading: state => state.app.isComponentLoading,
    }),
    isBeingWatched() {
      return this.media.id === this.playingVideoId
    },
    type() {
      const {
        media: { content_type },
      } = this
      return content_type
    },
    contentDuration() {
      const {
        media: { duration_in_seconds: seconds },
      } = this
      return seconds ? duration.durationFromSecs(seconds) : ''
    },
    contentIcon() {
      const { type } = this
      const iconMap = {
        quiz: QuizIcon,
        video: VideoIcon,
        lab: LabIcon,
      }
      return iconMap[type] || VideoIcon
    },
    title() {
      const {
        media: { name },
      } = this
      if (this.type === 'video') {
        return name
      }
      const {
        media: { metadata },
      } = this
      return metadata?.quiz_title ? metadata.quiz_title : name
    },
    contentIsFinished() {
      const {
        media: { user_status },
      } = this
      return user_status === CONSTANTS.CONTENT_USER_STATUS.FINISHED
    },
  },
  methods: {
    startMedia() {
      const {
        type,
        media: { id },
      } = this
      if (id !== this.playingVideoId) this.levelIsLoading = true
      if (type === 'video') {
        this.$store.dispatch('app/setIsComponentLoading', true)
        this.$store.dispatch('content/setPlayingVideoId', '')
        return this.$nextTick(() => {
          this.$store.dispatch('content/setPlayingVideoId', id)
        })
      }
      if (type === 'quiz') {
        this.$store.dispatch('content/setPlayingVideoId', '')
        this.$store.dispatch('app/setIsComponentLoading', true)
        return this.$store.dispatch('content/setCurrentQuizId', id)
      }
      if (type === 'lab') {
        this.$store.dispatch('content/setPlayingVideoId', '')
        this.$store.dispatch('content/setCurrentQuizId', '')
        const route = `${this.$route.name}-lab`
        this.levelIsLoading = false
        this.$router.push({
          name: route,
          params: { labId: id },
        })
      }
    },
  },
}
</script>

<style lang="scss">
.bootcamp-level {
  $this: &;
  width: 100%;
  background: var(--light-gray);
  padding: 15px 10px;
  margin: 5px 0 7px 0;
  display: flex;
  align-items: center;
  outline: none;
  border: none;
  color: var(--white);
  cursor: pointer;
  border-radius: 5px;
  border-left: 5px solid transparent;
  &:hover {
    #{$this}__title {
      color: var(--orange);
    }
  }
  &__selected {
    border-color: var(--orange);
  }
  &__finished {
    border-color: var(--green);
  }
  &__icon-wrapper {
    height: 25px;
    width: 20px;
    position: relative;
    margin: 0 15px 0 0;
  }
  &__loader {
    position: absolute;
    display: block;
    width: 20px;
    top: -7px;
    left: -7px;
    padding: 0;
  }
  &__icon {
    height: 20px;
    width: 20px;
    margin: 5px 0 0 0;
    fill: var(--lighter-gray);
    path {
      stroke: var(--lighter-gray);
    }
  }
  &__title {
    margin: 0;
    padding: 0;
    text-decoration: underline;
    font-size: rem(16px);
    text-align: left;
  }
  &__duration {
    margin: 0 0 0 auto;
    padding: 0;
    font-size: rem(16px);
  }
}
</style>
